/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import { toast } from 'react-toastify';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';

const InstitutionRoleManagement = () => {
  const [institutionRoles, setInstitutionRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    role: '',
    description: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const filteredRoles = institutionRoles.filter(role => {
    return searchTerm === '' ||
      role.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredRoles,
    { defaultSortColumn: 'role', defaultSortOrder: 'asc' }
  );

  useEffect(() => {
    fetchInstitutionRoles();
  }, []);

  const fetchInstitutionRoles = async () => {
    try {
      const response = await axios.get('/api/institution-roles');
      setInstitutionRoles(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching institution roles:', error);
      toast.error('Failed to fetch institution roles');
      setLoading(false);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        role: role.role,
        description: role.description
      });
    } else {
      setEditingRole(null);
      setFormData({
        role: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRole(null);
    setFormData({
      role: '',
      description: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.role.trim() || !formData.description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      if (editingRole) {
        await axios.put(`/api/institution-roles/${editingRole._id}`, formData);
        toast.success('Institution role updated successfully');
        handleCloseDialog();
      } else {
        await axios.post('/api/institution-roles', formData);
        toast.success('Institution role created successfully');
        setFormData({ role: '', description: '' });
      }
      
      fetchInstitutionRoles();
    } catch (error) {
      console.error('Error saving institution role:', error);
      toast.error(error.response?.data?.message || 'Failed to save institution role');
    }
  };

  const handleDeleteClick = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/institution-roles/${roleToDelete._id}`);
      toast.success('Institution role deleted successfully');
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      fetchInstitutionRoles();
    } catch (error) {
      console.error('Error deleting institution role:', error);
      toast.error(error.response?.data?.message || 'Failed to delete institution role');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading institution roles...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        Institution Role Management
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Institution roles define the types of users in the platform (e.g., Faculty, Student, Admin). 
        Roles marked with a shield icon are used in platform logic to control permissions and cannot be deleted or renamed.
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          label="Search Roles"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by role name, description..."
          sx={{ minWidth: 300 }}
        />
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Institution Role
        </Button>
        <Button variant="outlined" onClick={resetSort}>Reset Sort</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <SortableTableHeader column="role" label="Role" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
              </TableCell>
              <TableCell>
                <SortableTableHeader column="description" label="Description" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((role) => (
              <TableRow key={role._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {role.role}
                    </Typography>
                    {role.is_protected && (
                      <Tooltip title="Used in platform logic for permissions — cannot be deleted or renamed" arrow>
                        <Chip
                          icon={<ShieldIcon sx={{ fontSize: 16 }} />}
                          label="Platform"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ cursor: 'help' }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{role.description}</TableCell>
                <TableCell align="center">
                  <Tooltip title={role.is_protected ? 'You can update the description only' : 'Edit role'}>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(role)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={role.is_protected ? 'This role is used in platform logic and cannot be deleted' : 'Delete role'}>
                    <span>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(role)}
                        size="small"
                        disabled={role.is_protected}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {institutionRoles.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="textSecondary">
                    No institution roles found. Click "Add Institution Role" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Institution Role' : 'Add Institution Role'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            variant="outlined"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            placeholder="e.g., Faculty, Student, Postdoc"
            disabled={editingRole?.is_protected}
            helperText={editingRole?.is_protected ? 'This role name is used in platform logic and cannot be changed' : ''}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe this role..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the institution role "{roleToDelete?.role}"?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This action cannot be undone. Users currently assigned to this role will need to be reassigned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstitutionRoleManagement;
